<template>
    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
        <Panel
            class="card rounded rounded-10"
        >
            <template #header class="card-header">
                <h5>
                    <i class="fas fa-info-circle mr-2"></i>Network Access List Details
                </h5>
            </template>
            <template #icons v-if="editable">
                <button
                    v-if="editable && !editing"
                    type="button"
                    :class="'btn btn-sm ' + pageButton + ' text-white'"
                    @click="toggleEdit"
                    title="Edit"
                    style="float: right;"
                >
                    <i class="fas fa-pencil-alt mr-2"></i>Edit
                </button>
                <button
                    v-if="editing"
                    type="button"
                    class="btn btn-sm btn-success float-right"
                    @click="saveWhitelist"
                    :disabled="loading"
                    title="Save"
                >
                    <i class="fas fa-save mr-2"></i>Save
                </button>
                <button
                    v-if="editing"
                    type="button"
                    class="btn btn-sm btn-warning float-right mr-2"
                    @click="cancelEdit"
                    title="Cancel"
                >
                    <i class="fas fa-times mr-2"></i>Cancel
                </button>
            </template>

            <div id="collapseWhitelist" class="collapse show">
                <div class="card-body">
                    <div v-if="!selectedWhitelist || loading" class="text-center py-4">
                        <div v-if="loading"  class="d-flex flex-column align-items-center justify-content-center py-4 text-dark">
                            <i class="fa-solid fa-spinner fa-spin fa-4x"></i>
                        </div>
                    </div>

                    <div v-else>
                        <table class="table table-striped info-box-small">
                            <tbody>
                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>ID</strong></td>
                                <td>{{ selectedWhitelist.id }}</td>
                            </tr>

                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Name</strong></td>
                                <td class="d-flex flex-column">
                                    <InputText
                                        v-if="editing"
                                        v-model="selectedWhitelist.attributes.name"
                                        class="p-inputtext-sm"
                                        placeholder="Enter name"
                                        maxlength="30"
                                    />
                                    <span v-else class="d-flex flex-column">{{ selectedWhitelist.attributes.name }}</span>
                                    <small v-if="editing && nameError" class="p-error">{{ nameError }}</small>
                                </td>
                            </tr>

                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Description</strong></td>
                                <td class="d-flex flex-column">
                                    <InputText
                                        v-if="editing"
                                        v-model="selectedWhitelist.attributes.description"
                                        class="p-inputtext-sm"
                                        placeholder="Enter description"
                                        maxlength="100"
                                    />
                                    <span v-else class="description-text">{{ selectedWhitelist.attributes.description || '(None)' }}</span>
                                    <small v-if="editing && descriptionError" class="p-error">{{ descriptionError }}</small>
                                </td>
                            </tr>

                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Provider</strong></td>
                                <td>{{ selectedWhitelist.attributes.provider }}</td>
                            </tr>

                            <tr v-if="selectedWhitelist.attributes.status">
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Status</strong></td>
                                <td><span :class="'badge badge-' + statusBadge">{{ statusLabel }}</span></td>
                            </tr>

                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Type</strong></td>
                                <td><span :class="'badge badge-' + typeBadge">{{ typeLabel }}</span></td>
                            </tr>

                            <tr>
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Sims Count</strong></td>
                                <td>{{ selectedWhitelist.attributes.simsCount }}</td>
                            </tr>

                            <tr v-if="selectedWhitelist.attributes.dateCreated">
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Date Created</strong></td>
                                <td>{{ selectedWhitelist.attributes.dateCreated }}</td>
                            </tr>

                            <tr v-if="selectedWhitelist.attributes.dateModified">
                                <td><i :class="'ion ion-stop text-' + pageInfo"></i></td>
                                <td><strong>Last Modified</strong></td>
                                <td>{{ selectedWhitelist.attributes.dateModified }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Panel>
    </div>
</template>

<script>
import axios from 'axios';
import { cloneDeep } from 'lodash';
import Toast from 'primevue/toast';
import ProgressSpinner from 'primevue/progressspinner';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

export default {
    name: 'WhitelistDetails',
    props: {
        whitelistId: {
            type: String,
            required: true,
        },
        whitelist: {
            type: Object,
            default: null,
        },
        editable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['whitelist-updated', 'fetch-error'],
    components: {
        Toast,
        ProgressSpinner,
        InputText,
        Button,
    },
    data() {
        return {
            editing: false,
            loading: false,
            selectedWhitelist: this.whitelist ? cloneDeep(this.whitelist) : null,
            originalName: null,
            originalDescription: null,
        };
    },
    mounted() {
        if (!this.whitelist) {
            this.fetchWhitelist();
        }
    },
    computed: {
        pageButton() {
            return this.$themeSettings.pageButton;
        },
        pageInfo() {
            return this.$themeSettings.pageInfo;
        },
        statusLabel() {
            const status = this.selectedWhitelist?.attributes?.status;
            return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
        },
        statusBadge() {
            const status = this.selectedWhitelist?.attributes?.status;
            if (status === 'enabled') {
                return 'success';
            } else if (status === 'disabled') {
                return 'dark';
            } else if (status === 'retired') {
                return 'danger';
            }
            return 'info';
        },
        typeLabel() {
            return this.selectedWhitelist?.attributes?.isBootstrap ? 'Bootstrap' : 'Operational';
        },
        typeBadge() {
            return this.selectedWhitelist?.attributes?.isBootstrap ? 'success' : 'info';
        },
        nameError() {
            if (this.editing && !this.selectedWhitelist.attributes.name) {
                return 'Name is required';
            }
            else if (this.editing && this.selectedWhitelist?.attributes?.name?.length >= 30) {
                return 'Name should be 30 characters or fewer';
            }
            return '';
        },
        descriptionError() {
            if (this.editing && this.selectedWhitelist?.attributes?.description?.length >= 100) {
                return 'Description should be 100 characters or fewer';
            }
            return '';
        },
    },
    methods: {
        fetchWhitelist() {
            this.loading = true;
            axios
                .get(`/network-access/${this.whitelistId}`, {
                    headers: { Accept: 'application/json' },
                })
                .then((result) => {
                    this.selectedWhitelist = result.data.data;
                })
                .catch((err) => {
                    this.$emit('fetch-error', {
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load whitelist details',
                        life: 3000,
                    });
                }).finally(() => {
                    this.loading = false;
            });
        },
        toggleEdit() {
            this.editing = !this.editing;
        },
        saveWhitelist() {
            const name = this.selectedWhitelist.attributes.name?.trim();
            const description = this.selectedWhitelist.attributes.description?.trim();
            if (name.length > 30) {
                this.$emit('whitelist-updated', {
                    severity: 'error',
                    summary: 'Name too long',
                    detail: 'The name should be 30 characters or fewer.',
                    life: 4000,
                });
                return;
            }

            if (description && description.length > 100) {
                this.$emit('whitelist-updated', {
                    severity: 'error',
                    summary: 'Description too long',
                    detail: 'The description should be at most 100 characters.',
                    life: 4000,
                });
            }
            this.loading = true;

            axios
                .patch(`/network-access/${this.whitelistId}`, {
                    name: this.selectedWhitelist.attributes.name,
                    description: this.selectedWhitelist.attributes.description,
                })
                .then((res) => {
                    if((res.data?.result.toLowerCase() === 'success')) {
                        this.$emit('whitelist-updated', {
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Whitelist updated successfully.',
                            life: 3000,
                        });
                    } else {
                        this.$emit('whitelist-updated', {
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to update whitelist.',
                            life: 3000,
                        });
                    }
                    this.editing = false;
                })
                .catch((err) => {
                    this.$emit('whitelist-updated', {
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update whitelist.',
                        life: 3000,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        cancelEdit() {
            this.editing = false;
            this.selectedWhitelist.attributes.name = this.originalName;
            this.selectedWhitelist.attributes.description = this.originalDescription;
        },
    },
    watch: {
        whitelistId() {
            this.fetchWhitelist();
        },
        editing(isEditing) {
            if(isEditing) {
                this.originalName = this.selectedWhitelist.attributes.name?.trim();
                this.originalDescription = this.selectedWhitelist.attributes.description?.trim();
            }
        }
    },
};
</script>
<style scoped>
.description-text {
    text-wrap: auto;
}
</style>
